import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { TESTIMONIALS } from "../data/portfolio";

export default function TestimonialSlider() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const count = TESTIMONIALS.length;

  const go = useCallback((next) => {
    setDir(next > index || (index === count - 1 && next === 0) ? 1 : -1);
    setIndex((next + count) % count);
  }, [index, count]);

  useEffect(() => {
    const t = setInterval(() => {
      setDir(1);
      setIndex((i) => (i + 1) % count);
    }, 6000);
    return () => clearInterval(t);
  }, [count]);

  const item = TESTIMONIALS[index];

  return (
    <div data-testid="testimonial-slider" className="relative">
      <div className="relative min-h-[320px] md:min-h-[280px] flex items-center">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.blockquote
            key={index}
            custom={dir}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl"
            data-testid={`testimonial-${index}`}
          >
            <p className="font-display text-3xl md:text-5xl tracking-tighter font-medium leading-[1.05]">
              "{item.quote}"
            </p>
            <footer className="mt-8 flex items-center gap-3 text-neutral-400">
              <span className="h-px w-8 bg-neutral-500" />
              <span className="text-base">{item.author}</span>
            </footer>
          </motion.blockquote>
        </AnimatePresence>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="testimonial-dots">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => go(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-white" : "w-1.5 bg-white/30 hover:bg-white/60"}`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => go(index - 1)}
            aria-label="Previous testimonial"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors duration-300 hover:bg-white hover:text-black"
            data-testid="testimonial-prev"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            onClick={() => go(index + 1)}
            aria-label="Next testimonial"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors duration-300 hover:bg-white hover:text-black"
            data-testid="testimonial-next"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
